import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo, CampoSelecao } from '@/componentes/Campo';
import { Barra, Carregando, ControleCarregando, EsqueletoDeLista } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Janela } from '@/componentes/JanelaDeAcao';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import {
  EsqueletoTabela,
  QuadroTabela,
  RodapeTabela,
  Tabela,
  TextoDaCelula,
  type Coluna,
  type ColunaDoEsqueleto,
} from '@/componentes/Tabela';
import { formatarData, formatarDataHora, formatarDia, formatarInteiro } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { anunciar, retornoDe } from '@/lib/retorno';
import type { AcertoDaApi } from '@/modulos/combustivel/folhas';
import { useSessaoEmMemoria } from '@/modulos/sessao/memoria';

import { useCadastrosDaOperacao } from './consultas';
import { maquinasDeHorimetro } from './correcao';
import {
  apiDaFolhaDeTurno,
  mesCorrente,
  montarEnvioDoTurno,
  recusaDaLinha,
  ROTULO_DO_CAMPO_DO_TURNO,
  ROTULO_DO_INSTRUMENTO,
  turnoSeguinte,
  turnosParaConferir,
  type CampoDoTurno,
  type CausaDaFolha,
  type FolhaDeTurnoDaApi,
  type Instrumento,
  type LeituraDeTurnoDaApi,
  type TurnoEmConferencia,
} from './folhasDeTurno';
import { usePaginacao } from './paginacao';
import { apagarRascunho, guardarRascunho, lerRascunho } from './rascunho';
import { contar, fraseDoErro } from './texto';
import { opcaoDaMaquina } from '@/modulos/equipamentos/opcao-da-maquina';
import { opcaoDaPessoa, useOpcoesComVisualDaFrota } from '@/modulos/equipamentos/opcoesComVisual';

/**
 * FOLHAS DE TURNO (módulo 03; 0041; decisões 9, 10 e 12).
 *
 * O campo alimenta o sistema pela folha impressa, e até aqui só o combustível
 * tinha folha. Aqui se emite a folha de uma máquina num período, se envia a
 * imagem escaneada, e quem escaneou confere o que a IA leu, campo a campo,
 * olhando o papel ao lado, antes de virar apontamento — pela mesma validação
 * do formulário. A taxa de acerto por instrumento fica no topo.
 */

const CHAVE = ['operacao', 'folhas'] as const;
const dataCurta = (iso: string) => iso.split('-').reverse().join('/');

/** A recusa do servidor, na caixa padrão: a frase fechada (p.23) e o que fazer (regra 10). */
function CaixaDeRecusa({ falha, classe = '' }: { readonly falha: unknown; readonly classe?: string }) {
  const { erro, comoResolver } = explicarFalha(falha);
  return (
    <div role="alert" className={`flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4${classe}`}>
      <span className="txt-corpo text-tinta">{fraseDoErro(erro)}</span>
      <span className="txt-apoio text-tinta-secundaria">{comoResolver}</span>
    </div>
  );
}

export function PaginaFolhasDeTurno() {
  const [emitindo, setEmitindo] = useState(false);
  /* Cada abertura da janela é uma emissão nova: a chave refaz o formulário
     limpo, e não com o que ficou pela metade da última vez. */
  const [emissao, setEmissao] = useState(0);
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const [conferindo, setConferindo] = useState<{ folhaId: string; leituraId: string } | null>(null);
  const lista = useQuery({ queryKey: [...CHAVE, 'lista'], queryFn: () => apiDaFolhaDeTurno.folhas() });
  const folhas = useMemo(() => lista.data?.folhas ?? [], [lista.data]);
  const { visiveis, rodape } = usePaginacao(folhas);

  function abrirEmissao() {
    setEmissao((atual) => atual + 1);
    setEmitindo(true);
  }

  if (conferindo) {
    return (
      <div className="min-w-0 flex-1 overflow-auto">
        {/* A MARGEM DA R01 (p.28): 32/24/16 px pela largura, como toda página. */}
        <div className="pagina">
          <ConferenciaDoTurno
            folhaId={conferindo.folhaId}
            leituraId={conferindo.leituraId}
            aoTerminar={() => {
              setConferindo(null);
            }}
          />
        </div>
      </div>
    );
  }

  const folha = folhas.find((f) => f.id === selecionada) ?? null;

  return (
    <div className="flex min-h-0 min-w-0 flex-1">
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          {/* Sobretítulo com o nome da área, como as outras telas da Operação
              (p.23: a palavra da obra, e não o número interno do v6). */}
          <CabecalhoPagina
            sobretitulo="Operação"
            titulo="Folhas de turno"
            subtitulo="A folha impressa de uma máquina num período. Quem opera preenche o turno; quem escaneia confere o que a IA leu e lança."
            acoes={
              <Botao nivel="primario" onClick={abrirEmissao}>
                Emitir folha
              </Botao>
            }
          />

          {lista.data && !lista.data.leituraLigada ? (
            <div role="status" className="flex flex-col gap-1 rounded-controle bg-informacao-fundo px-4 py-2">
              <span className="txt-apoio font-semibold text-informacao-texto">Leitura pela IA desligada neste ambiente</span>
              <span className="txt-apoio text-tinta-secundaria">
                A folha pode ser emitida e impressa, mas a imagem escaneada não é lida. Enquanto isso, aponte pelo formulário.
              </span>
            </div>
          ) : null}

          <EmitirFolha
            key={emissao}
            aberta={emitindo}
            aoFechar={() => {
              setEmitindo(false);
            }}
            aoEmitir={(f) => {
              setEmitindo(false);
              setSelecionada(f.id);
            }}
          />

          <AcertoPorInstrumento acerto={lista.data?.acerto ?? []} carregando={lista.isPending} />

          <div data-painel-origem>
            <Tabela
              rotulo="Folhas de turno emitidas"
              colunas={COLUNAS}
              linhas={visiveis}
              chaveDaLinha={(f) => f.id}
              carregando={lista.isPending}
              alturaDupla
              selecionada={selecionada}
              aoSelecionar={(f) => {
                setSelecionada(f.id);
              }}
              erro={
                lista.isError ? (
                  <EstadoVazio
                    titulo="Não foi possível carregar as folhas"
                    descricao={explicarFalha(lista.error).comoResolver}
                    acao={
                      <Botao
                        nivel="secundario"
                        onClick={() => {
                          void lista.refetch();
                        }}
                      >
                        Carregar outra vez
                      </Botao>
                    }
                  />
                ) : null
              }
              vazio={
                <EstadoVazio
                  titulo="Nenhuma folha de turno emitida"
                  descricao="Emita a folha da primeira máquina: ela sai com a máquina e o período impressos, e o campo só escreve os turnos."
                  acao={
                    <Botao nivel="secundario" onClick={abrirEmissao}>
                      Emitir folha
                    </Botao>
                  }
                />
              }
              /* RODAPÉ DA p.18: contagem à esquerda, paginação ao centro, itens
                 por página à direita — como nas outras listas da Operação. */
              rodape={<RodapeTabela {...rodape} />}
            />
          </div>
        </div>
      </div>

      {folha ? (
        <PainelDaFolha
          key={folha.id}
          folha={folha}
          leituraLigada={lista.data?.leituraLigada ?? false}
          aoConferir={(leituraId) => {
            setConferindo({ folhaId: folha.id, leituraId });
          }}
          aoFechar={() => {
            setSelecionada(null);
          }}
        />
      ) : null}
    </div>
  );
}

/** O cabeçalho da grade de conferência, para o esqueleto dela (a grade é desenhada à mão, abaixo). */
const COLUNAS_DA_CONFERENCIA: readonly ColunaDoEsqueleto[] = [
  { chave: 'linha', rotulo: 'Linha' },
  { chave: 'data', rotulo: 'Data' },
  { chave: 'turno', rotulo: 'Turno' },
  { chave: 'horimetro', rotulo: 'Horímetro', alinhamento: 'numero' },
  { chave: 'divisao', rotulo: 'Divisão do turno (h)', alinhamento: 'numero' },
  { chave: 'operador', rotulo: 'Operador' },
];

const COLUNAS: readonly Coluna<FolhaDeTurnoDaApi>[] = [
  { chave: 'codigo', rotulo: 'Folha', celula: (f) => <span className="codigo">{f.codigo}</span> },
  {
    chave: 'maquina',
    rotulo: 'Máquina',
    celula: (f) => (
      <span className="flex flex-col">
        {/* Código em monoespaçada, como o da folha ao lado (p.18). */}
        <span className="codigo text-tinta">{f.equipamentoCodigo}</span>
        <TextoDaCelula texto={f.equipamentoDescricao} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  { chave: 'periodo', rotulo: 'Período', celula: (f) => `${dataCurta(f.de)} a ${dataCurta(f.ate)}` },
  {
    chave: 'leituras',
    rotulo: 'Leituras',
    alinhamento: 'numero',
    /* Secundária (R01, p.28): a contagem está na aba "Leituras (N)" do painel. */
    prioridade: 'secundaria',
    celula: (f) => formatarInteiro(f.leituras),
  },
  { chave: 'lancados', rotulo: 'Turnos lançados', alinhamento: 'numero', celula: (f) => formatarInteiro(f.lancados) },
];

function AcertoPorInstrumento({
  acerto,
  carregando,
}: {
  readonly acerto: readonly AcertoDaApi[];
  readonly carregando: boolean;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="txt-secao text-tinta">Quanto a leitura acerta, por instrumento?</h2>
      {carregando ? (
        /* Carregando (p.32) na forma da taxa: o instrumento, a taxa e a
           contagem de cada um. Antes, a frase de "nenhuma conferida ainda"
           aparecia enquanto a resposta vinha — e afirmava o que não sabia. */
        <Carregando oQue="Carregando a taxa de acerto da leitura" className="flex flex-wrap gap-6">
          {['w-40', 'w-48'].map((largura) => (
            <div key={largura} className="flex min-w-0 flex-col gap-2 py-1">
              <Barra className="h-2 w-16" />
              <Barra className="h-3 w-24" />
              <Barra className={`h-3 ${largura}`} />
            </div>
          ))}
        </Carregando>
      ) : acerto.length === 0 ? (
        <p className="txt-apoio text-tinta-secundaria">Nenhuma folha de turno conferida ainda: a taxa aparece depois da primeira conferência.</p>
      ) : (
        <ul className="flex flex-wrap gap-6">
          {acerto.map((a) => (
            <li key={a.instrumento} className="flex min-w-0 flex-col">
              <span className="txt-legenda font-normal text-tinta-secundaria">{ROTULO_DO_INSTRUMENTO[a.instrumento]}</span>
              <span className="txt-corpo tabular-nums text-tinta">
                {a.taxaDeAcerto === null ? '—' : `${formatarInteiro(Math.round(a.taxaDeAcerto * 100))}% dos campos`}
              </span>
              {/* Plural de gente, e não "(s)" (p.23). */}
              <span className="txt-apoio tabular-nums text-tinta-secundaria">
                {`${contar(a.leituras, 'leitura', 'leituras')}; ${formatarInteiro(a.camposCorrigidos)} de ${contar(a.camposLidos, 'campo lido', 'campos lidos')} com correção`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** A lista de máquinas, montada só com a janela aberta: é ela que busca os cadastros. */
function SeletorDeMaquina({ valor, aoMudar }: { readonly valor: string; readonly aoMudar: (valor: string) => void }) {
  const comVisualDaFrota = useOpcoesComVisualDaFrota();
  const id = useId();
  const cadastros = useCadastrosDaOperacao();
  /* As máquinas ainda vêm (p.32): o rótulo fica e o controle vira barra — a
     lista só com "Escolha a máquina" deixava escolher o nada. */
  if (cadastros.isPending) {
    return (
      <Campo id={id} rotulo="Máquina">
        <ControleCarregando oQue="Carregando as máquinas" />
      </Campo>
    );
  }
  return (
    <CampoSelecao
      rotulo="Máquina"
      valor={valor}
      rotuloVazio="Escolha a máquina"
      /* A folha de turno é por horímetro (0086): o caminhão de odômetro não tem folha de turno. */
      opcoes={comVisualDaFrota(maquinasDeHorimetro(cadastros.data?.equipamentos ?? []).map((e) => opcaoDaMaquina(e)))}
      aoMudar={aoMudar}
      adesivo={{ porQueFora: 'a folha de turno é só de máquina com horímetro' }}
    />
  );
}

/**
 * A JANELA "EMITIR FOLHA" (R01, p.29, o exemplo corrigido): cabeçalho e
 * rodapé fixos, só o corpo rola. Os botões vão no RODAPÉ da janela, e não no
 * fim do formulário — em tela cheia (abaixo de 768) eles ficavam no meio da
 * tela, logo abaixo dos campos. "Cancelar" à esquerda, "Emitir folha" à
 * direita, com o mesmo nome do gatilho e do título.
 */
function EmitirFolha({
  aberta,
  aoFechar,
  aoEmitir,
}: {
  readonly aberta: boolean;
  readonly aoFechar: () => void;
  readonly aoEmitir: (f: FolhaDeTurnoDaApi) => void;
}) {
  const base = useId();
  const cliente = useQueryClient();
  const inicial = mesCorrente(new Date());
  const [equipamentoId, setEquipamentoId] = useState('');
  const [de, setDe] = useState(inicial.de);
  const [ate, setAte] = useState(inicial.ate);
  const emitir = useMutation({
    mutationFn: () => apiDaFolhaDeTurno.emitir({ equipamentoId, de, ate }),
    meta: {
      retorno: retornoDe<FolhaDeTurnoDaApi>((f) => ({
        titulo: 'Folha de turno emitida',
        alvo: { codigo: f.equipamentoCodigo, nome: f.equipamentoDescricao },
        fatos: [`${formatarDia(f.de)} a ${formatarDia(f.ate)}`],
        codigo: f.codigo,
      })),
    },
    onSuccess: (f) => {
      void cliente.invalidateQueries({ queryKey: CHAVE });
      aoEmitir(f);
    },
  });
  function enviar(evento: FormEvent) {
    evento.preventDefault();
    emitir.mutate();
  }
  return (
    <Janela
      titulo="Emitir folha de turno"
      aberta={aberta}
      aoFechar={aoFechar}
      acoes={
        <>
          <Botao nivel="secundario" type="button" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao nivel="primario" type="submit" form={`${base}-form`} disabled={emitir.isPending || equipamentoId === ''}>
            {emitir.isPending ? 'Emitindo…' : 'Emitir folha'}
          </Botao>
        </>
      }
    >
      {/* Grade da R01 (p.28): a máquina é larga, as duas datas numa coluna
          cada. Dentro da janela, a grade mede o espaço e cai para duas colunas. */}
      <form id={`${base}-form`} className="formulario-grade" onSubmit={enviar}>
        <div className="campo-largo">
          <SeletorDeMaquina valor={equipamentoId} aoMudar={setEquipamentoId} />
        </div>
        <Campo id={`${base}-de`} rotulo="Do dia">
          <input id={`${base}-de`} type="date" className="controle w-full" value={de} onChange={(e) => { setDe(e.target.value); }} />
        </Campo>
        <Campo id={`${base}-ate`} rotulo="Até o dia" ajuda="Uma folha por mês.">
          <input id={`${base}-ate`} type="date" className="controle w-full" value={ate} onChange={(e) => { setAte(e.target.value); }} />
        </Campo>
        {emitir.isError ? <CaixaDeRecusa falha={emitir.error} classe="campo-linha" /> : null}
      </form>
    </Janela>
  );
}

function PainelDaFolha({
  folha,
  leituraLigada,
  aoConferir,
  aoFechar,
}: {
  readonly folha: FolhaDeTurnoDaApi;
  readonly leituraLigada: boolean;
  readonly aoConferir: (leituraId: string) => void;
  readonly aoFechar: () => void;
}) {
  const detalhe = useQuery({ queryKey: [...CHAVE, 'folha', folha.id], queryFn: () => apiDaFolhaDeTurno.folha(folha.id) });
  return (
    <PainelDetalhe
      /* Identidade da p.19: o código da folha pequeno, em monoespaçada, e o
         nome da máquina como título. */
      codigo={folha.codigo}
      titulo={folha.equipamentoDescricao}
      subtitulo={`${dataCurta(folha.de)} a ${dataCurta(folha.ate)}`}
      dadosChave={
        <>
          <DadoChave rotulo="Máquina">
            <span className="codigo">{folha.equipamentoCodigo}</span>
          </DadoChave>
          <DadoChave rotulo="Emitida">{formatarData(new Date(folha.emitidaEm))}</DadoChave>
          <DadoChave rotulo="Turnos lançados">
            <span className="tabular-nums">{formatarInteiro(folha.lancados)}</span>
          </DadoChave>
        </>
      }
      abas={[
        {
          id: 'ler',
          rotulo: 'Ler escaneamento',
          conteudo: leituraLigada ? (
            <Escanear folhaId={folha.id} aoLer={aoConferir} />
          ) : (
            <p className="txt-apoio text-tinta-secundaria">A leitura pela IA está desligada neste ambiente. Aponte pelo formulário.</p>
          ),
        },
        {
          id: 'leituras',
          rotulo: `Leituras (${formatarInteiro(folha.leituras)})`,
          conteudo: (
            <ListaDeLeituras leituras={detalhe.data?.leituras ?? []} carregando={detalhe.isPending} aoConferir={aoConferir} />
          ),
        },
      ]}
      acoes={[
        <Botao
          key="imprimir"
          nivel="secundario"
          onClick={() => {
            window.open(`/impressao/folha-de-turno/${folha.id}`, '_blank', 'noopener');
          }}
        >
          Imprimir folha
        </Botao>,
      ]}
      aoFechar={aoFechar}
    />
  );
}

function Escanear({ folhaId, aoLer }: { readonly folhaId: string; readonly aoLer: (leituraId: string) => void }) {
  const base = useId();
  const cliente = useQueryClient();
  const entrada = useRef<HTMLInputElement>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [instrumento, setInstrumento] = useState<Instrumento | ''>('');
  const ler = useMutation({
    mutationFn: () => apiDaFolhaDeTurno.escanear(folhaId, instrumento as Instrumento, arquivo as File),
    meta: {
      retorno: retornoDe<Awaited<ReturnType<typeof apiDaFolhaDeTurno.escanear>>>((r) => ({
        titulo: 'Folha de turno lida',
        tom: r.codigoConfere === false ? 'ressalva' : 'sucesso',
        ...(r.codigoLido ? { alvo: { codigo: r.codigoLido } } : {}),
        fatos: [
          contar(r.leitura.linhasLidas.length, 'turno lido', 'turnos lidos'),
          r.codigoConfere === false ? 'O código lido não é o desta folha' : null,
        ],
        efeito: 'Nada entra antes de você conferir os turnos',
      })),
    },
    onSuccess: (r) => {
      void cliente.invalidateQueries({ queryKey: CHAVE });
      aoLer(r.leitura.id);
    },
  });
  return (
    <div className="flex flex-col gap-4">
      <p className="txt-apoio text-pretty text-tinta-secundaria">
        Envie a folha escaneada ou fotografada, reta e inteira. A IA lê os turnos e marca onde teve dúvida; você confere
        olhando o papel, e só então os turnos entram.
      </p>
      {/* O SELETOR DE ARQUIVO NO DESENHO DO SISTEMA (p.15 e p.16): o nativo do
          navegador ("Escolher arquivo Nenhum arquivo escolhido") ficava colado
          ao topo da caixa de 36 px. Aqui é um botão secundário, com o nome do
          arquivo escolhido ao lado — como no cabeçalho das Importações. */}
      <div className="flex min-w-0 flex-col gap-1">
        <span className="txt-rotulo text-tinta-secundaria">Imagem da folha</span>
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <Botao
            nivel="secundario"
            type="button"
            aria-describedby={`${base}-arquivo-nome`}
            onClick={() => entrada.current?.click()}
          >
            Escolher imagem
          </Botao>
          <span id={`${base}-arquivo-nome`} className="txt-apoio min-w-0 wrap-anywhere text-tinta-secundaria">
            {arquivo ? arquivo.name : 'Nenhuma imagem escolhida'}
          </span>
        </div>
        <span className="txt-ajuda">PDF, PNG ou JPEG.</span>
        <input
          ref={entrada}
          id={`${base}-arquivo`}
          type="file"
          accept="application/pdf,image/png,image/jpeg"
          className="hidden"
          aria-label="Imagem da folha"
          onChange={(e) => {
            setArquivo(e.target.files?.[0] ?? null);
          }}
        />
      </div>
      <CampoSelecao
        rotulo="Com que a folha foi preenchida"
        valor={instrumento}
        rotuloVazio="Escolha"
        opcoes={(Object.keys(ROTULO_DO_INSTRUMENTO) as Instrumento[]).map((i) => ({ valor: i, rotulo: ROTULO_DO_INSTRUMENTO[i] }))}
        aoMudar={(v) => {
          setInstrumento(v as Instrumento | '');
        }}
      />
      {ler.isError ? <CaixaDeRecusa falha={ler.error} /> : null}
      <div>
        <Botao
          nivel="secundario"
          disabled={ler.isPending || arquivo === null || instrumento === ''}
          onClick={() => {
            ler.mutate();
          }}
        >
          {ler.isPending ? 'Lendo a folha…' : 'Ler e conferir'}
        </Botao>
      </div>
    </div>
  );
}

function ListaDeLeituras({
  leituras,
  carregando,
  aoConferir,
}: {
  readonly leituras: readonly LeituraDeTurnoDaApi[];
  readonly carregando: boolean;
  readonly aoConferir: (leituraId: string) => void;
}) {
  /* Carregando = esqueleto na forma do item (p.32): as duas linhas de cada
     leitura, e não uma frase que troca de tamanho quando a lista chega. */
  if (carregando) return <EsqueletoDeLista oQue="Carregando as leituras" itens={3} icone={false} />;
  if (leituras.length === 0) return <p className="txt-apoio text-tinta-secundaria">Nenhuma leitura desta folha ainda.</p>;
  return (
    <ul className="flex flex-col">
      {leituras.map((l) => (
        <li key={l.id} className="flex items-center justify-between gap-2 border-b border-borda-sutil py-2">
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="txt-corpo text-pretty text-tinta">{`${formatarDataHora(new Date(l.lidaEm))} · ${ROTULO_DO_INSTRUMENTO[l.instrumento]}`}</span>
            <span className="txt-apoio text-pretty text-tinta-secundaria">
              {l.conferidaEm
                ? `Conferida: ${contar(l.apontamentosLancados ?? 0, 'turno lançado', 'turnos lançados')}; ${formatarInteiro(l.camposCorrigidos ?? 0)} de ${contar(l.camposLidos ?? 0, 'campo lido', 'campos lidos')} com correção`
                : `${contar(l.linhasLidas.length, 'turno lido', 'turnos lidos')}, falta conferir`}
            </span>
          </span>
          {l.conferidaEm ? null : (
            /* Verbo + objeto (p.15): "Conferir" sozinho não diz o quê. */
            <Botao
              nivel="terciario"
              onClick={() => {
                aoConferir(l.id);
              }}
            >
              Conferir leitura
            </Botao>
          )}
        </li>
      ))}
    </ul>
  );
}

/* ========================================================================
 * A CONFERÊNCIA
 * ===================================================================== */

/** A recusa de UMA linha, presa às células do campo recusado (p.16: o erro fica abaixo do campo). */
interface RecusaDaLinha {
  /** `null` só se a recusa não achar a linha — aí ela vai em bloco. */
  readonly chave: string | null;
  readonly campos: readonly CampoDoTurno[];
  readonly frase: string;
}

const CAMPOS_DE_TEXTO_DA_LINHA = [
  'chave',
  'data',
  'turno',
  'horimetroInicial',
  'horimetroFinal',
  'producao',
  'espera',
  'deslocamento',
  'manutencao',
  'operadorId',
] as const;

/** O rascunho lido de volta só vale com a forma certa, linha a linha e campo a campo. */
function linhasDoRascunho(valor: unknown): TurnoEmConferencia[] | null {
  if (!Array.isArray(valor)) return null;
  const valida = (item: unknown) => {
    if (typeof item !== 'object' || item === null) return false;
    const l = item as Record<string, unknown>;
    return (
      CAMPOS_DE_TEXTO_DA_LINHA.every((campo) => typeof l[campo] === 'string') &&
      typeof l.incluir === 'boolean' &&
      (l.origem === null || typeof l.origem === 'number') &&
      Array.isArray(l.duvidas) &&
      l.duvidas.every((d) => typeof d === 'string') &&
      (l.turnoEscrito === null || typeof l.turnoEscrito === 'string') &&
      (l.operadorEscrito === null || typeof l.operadorEscrito === 'string')
    );
  };
  if (!valor.every(valida)) return null;
  /* O rascunho de antes da causa da parada (0086) não a tem: vale "sem causa". */
  return (valor as (TurnoEmConferencia & { causaId?: unknown; causaEscrita?: unknown })[]).map((l) => ({
    ...l,
    causaId: typeof l.causaId === 'string' ? l.causaId : '',
    causaEscrita: typeof l.causaEscrita === 'string' ? l.causaEscrita : null,
  }));
}

/* A grade fica ao lado do papel a partir de 1280 px (R01, p.28); abaixo,
   empilhada. As duas colunas e o esqueleto usam a mesma divisão. */
const LADO_A_LADO = 'grid gap-6 min-[1280px]:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] min-[1280px]:items-start';

/**
 * A CONFERÊNCIA (decisão 12): o papel de um lado, o que a IA leu do outro.
 * Campo lido com dúvida vem marcado com a palavra "conferir" — nunca só com
 * cor. Linha que a IA inventou se desmarca; linha que ela não viu se
 * acrescenta. O botão lança tudo de uma vez, pela validação do formulário, e
 * a conferência não se refaz.
 *
 * RASCUNHO AUTOMÁTICO (p.16): são dezenas de campos, onze por linha lida. O
 * que se corrige fica guardado neste navegador, com o usuário e a leitura na
 * chave, até lançar — sair da tela, recarregar ou perder a conexão não perde
 * a correção feita.
 */
function ConferenciaDoTurno({
  folhaId,
  leituraId,
  aoTerminar,
}: {
  readonly folhaId: string;
  readonly leituraId: string;
  readonly aoTerminar: () => void;
}) {
  const base = useId();
  const cliente = useQueryClient();
  const usuario = useSessaoEmMemoria();
  const chaveDoRascunho = usuario === null ? null : `conferencia:${leituraId}:${usuario.usuarioId}`;
  const detalhe = useQuery({ queryKey: [...CHAVE, 'folha', folhaId], queryFn: () => apiDaFolhaDeTurno.folha(folhaId) });
  const imagem = useQuery({
    queryKey: [...CHAVE, 'imagem', leituraId],
    queryFn: () => apiDaFolhaDeTurno.imagem(leituraId),
    staleTime: 240_000,
  });
  const leitura = detalhe.data?.leituras.find((l) => l.id === leituraId) ?? null;
  const [recuperado, setRecuperado] = useState(() => {
    const guardado = chaveDoRascunho === null ? null : lerRascunho(chaveDoRascunho);
    const linhas = guardado === null ? null : linhasDoRascunho(guardado.valor);
    return guardado === null || linhas === null ? null : { linhas, salvoEm: guardado.salvoEm };
  });
  const [linhas, setLinhas] = useState<TurnoEmConferencia[] | null>(() => recuperado?.linhas ?? null);
  const [recusa, setRecusa] = useState<RecusaDaLinha | null>(null);
  const atuais = linhas ?? (leitura ? turnosParaConferir(leitura.linhasLidas) : []);
  const jaConferida = leitura !== null && leitura.conferidaEm !== null;
  const lancar = useMutation({
    mutationFn: (envio: Parameters<typeof apiDaFolhaDeTurno.conferir>[1]) => apiDaFolhaDeTurno.conferir(leituraId, envio),
    /* O retorno é toast (p.30, p.31) com o que entrou, e a tela volta à lista
       — antes ela era trocada inteira por um cartão de status. */
    meta: { silencioso: true },
    onSuccess: (r) => {
      if (chaveDoRascunho !== null) apagarRascunho(chaveDoRascunho);
      void cliente.invalidateQueries({ queryKey: ['operacao'] });
      const codigo = detalhe.data?.folha.codigo;
      anunciar({
        tom: 'sucesso',
        titulo: 'Folha de turno conferida e lançada',
        ...(codigo === undefined ? {} : { alvo: { codigo, nome: detalhe.data?.folha.equipamentoCodigo ?? '' } }),
        fatos: [
          contar(r.lancados, 'turno apontado', 'turnos apontados'),
          `a leitura acertou ${formatarInteiro(r.camposLidos - r.camposCorrigidos)} de ${contar(r.camposLidos, 'campo', 'campos')}`,
        ],
      });
      aoTerminar();
    },
  });

  /* Só o que a pessoa mexeu vira rascunho: `linhas` nulo é a leitura crua. */
  useEffect(() => {
    if (chaveDoRascunho === null || linhas === null || lancar.isSuccess) return;
    guardarRascunho(chaveDoRascunho, linhas);
  }, [linhas, chaveDoRascunho, lancar.isSuccess]);

  /* Leitura já conferida não se refaz: o rascunho dela não serve a ninguém. */
  useEffect(() => {
    if (jaConferida && chaveDoRascunho !== null) apagarRascunho(chaveDoRascunho);
  }, [jaConferida, chaveDoRascunho]);

  if (detalhe.isPending) {
    /* Esqueleto na forma final (p.32): o que não depende da leitura aparece —
       o título, o "Voltar sem lançar" (que já funciona), os títulos das duas
       colunas e o cabeçalho da grade —, e só o dado vira barra: o código da
       folha, a máquina, o papel e as linhas. O botão de lançar é barra porque
       o rótulo dele depende de quantas linhas vierem. A frase do status é a da
       grade. */
    return (
      <>
        <CabecalhoPagina
          sobretitulo="Folha de turno"
          titulo="Conferir a leitura"
          subtitulo="Olhe o papel e corrija o que a IA leu errado."
          acoes={
            <>
              <Botao nivel="secundario" onClick={aoTerminar}>
                Voltar sem lançar
              </Botao>
              <Barra className="h-[var(--controle-m)] w-32 rounded-controle" />
            </>
          }
        />
        <div className={LADO_A_LADO}>
          <section className="flex min-w-0 flex-col gap-2">
            <h2 className="txt-secao text-tinta">A folha escaneada</h2>
            <Barra className="aspect-[210/297] w-full max-w-[960px] rounded-card" />
          </section>
          <section className="flex min-w-0 flex-col gap-3">
            <h2 className="txt-secao text-tinta">O que a IA leu</h2>
            <QuadroTabela>
              <EsqueletoTabela rotulo="Leitura da folha de turno" colunas={COLUNAS_DA_CONFERENCIA} alturaDupla />
            </QuadroTabela>
          </section>
        </div>
      </>
    );
  }
  if (detalhe.isError || !detalhe.data || !leitura) {
    return (
      <EstadoVazio
        titulo="Não foi possível abrir a leitura"
        descricao={detalhe.isError ? explicarFalha(detalhe.error).comoResolver : 'Volte às folhas e abra a leitura de novo.'}
        acao={
          <Botao nivel="secundario" onClick={aoTerminar}>
            Voltar às folhas
          </Botao>
        }
      />
    );
  }
  const folha = detalhe.data.folha;
  const pessoas = detalhe.data.pessoas;
  const turnos = detalhe.data.turnos;
  const causas = detalhe.data.causas ?? [];
  const incluidas = atuais.filter((l) => l.incluir).length;
  const idDaLinha = (chave: string) => `${base}-${chave}`;
  const mudar = (chave: string, campo: Partial<TurnoEmConferencia>) => {
    setLinhas(atuais.map((l) => (l.chave === chave ? { ...l, ...campo } : l)));
    /* Mexer na linha recusada apaga a recusa dela; a das outras não existe. */
    if (recusa !== null && recusa.chave === chave) setRecusa(null);
  };

  function descartarRascunho() {
    if (chaveDoRascunho !== null) apagarRascunho(chaveDoRascunho);
    setLinhas(null);
    setRecusa(null);
    setRecuperado(null);
  }

  function lancarTurnos() {
    const r = montarEnvioDoTurno(atuais, folha);
    if ('recusa' in r) {
      /* A mesma regra, agora com o LUGAR: a primeira linha incluída que a
         regra recusa, e o campo. A frase vai para baixo da célula, e o foco
         para o campo — ele pode estar fora da vista. */
      const recusada = atuais.find((l) => l.incluir && recusaDaLinha(l, folha) !== null) ?? null;
      const achado = recusada === null ? null : recusaDaLinha(recusada, folha);
      if (recusada === null || achado === null) {
        setRecusa({ chave: null, campos: [], frase: r.recusa });
        return;
      }
      setRecusa({ chave: recusada.chave, campos: achado.campos, frase: fraseDoErro(achado.frase) });
      const primeiro = achado.campos[0];
      if (primeiro) document.getElementById(`${idDaLinha(recusada.chave)}-${primeiro}`)?.focus();
      return;
    }
    setRecusa(null);
    lancar.mutate(r.linhas);
  }

  return (
    <>
      <CabecalhoPagina
        sobretitulo={`Folha ${folha.codigo} · ${ROTULO_DO_INSTRUMENTO[leitura.instrumento]}`}
        titulo="Conferir a leitura"
        subtitulo={`${folha.equipamentoCodigo} — ${folha.equipamentoDescricao} · ${dataCurta(folha.de)} a ${dataCurta(folha.ate)}. Olhe o papel e corrija o que a IA leu errado.`}
        acoes={
          <>
            <Botao nivel="secundario" onClick={aoTerminar}>
              Voltar sem lançar
            </Botao>
            {/* Verbo + objeto, no plural certo (p.15, p.23); a contagem está
                ao lado da grade. */}
            <Botao nivel="primario" disabled={lancar.isPending || jaConferida} onClick={lancarTurnos}>
              {lancar.isPending ? 'Lançando…' : incluidas === 1 ? 'Lançar turno' : 'Lançar turnos'}
            </Botao>
          </>
        }
      />
      {/* Em bloco, só a recusa que NÃO é de campo: a do servidor. */}
      {lancar.isError ? <CaixaDeRecusa falha={lancar.error} /> : null}
      {recusa !== null && recusa.chave === null ? (
        <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
          <span className="txt-corpo text-tinta">{fraseDoErro(recusa.frase)}</span>
          <span className="txt-apoio text-tinta-secundaria">Corrija a linha na grade e lance de novo.</span>
        </div>
      ) : null}
      {recuperado === null || jaConferida ? null : (
        <div
          role="status"
          className="bloco-rebaixado flex flex-wrap items-center justify-between gap-2 px-4 py-2"
        >
          <span className="txt-apoio text-tinta-secundaria">
            {`Rascunho de ${formatarDataHora(new Date(recuperado.salvoEm))} recuperado: a conferência continua de onde você parou.`}
          </span>
          <Botao nivel="terciario" onClick={descartarRascunho}>
            Descartar rascunho
          </Botao>
        </div>
      )}

      {/* O PAPEL AO LADO DO QUE A IA LEU (R01, p.27: largura útil inteira). A
          partir de 1280 px, os dois lado a lado, com o papel preso no alto e
          rolagem própria; abaixo, empilhados. */}
      <div className={LADO_A_LADO}>
        <section className="flex min-w-0 flex-col gap-2 min-[1280px]:sticky min-[1280px]:top-[var(--margem-xl)] min-[1280px]:max-h-[calc(100vh-var(--barra-superior)-var(--margem-xl)*2)] min-[1280px]:overflow-auto">
          <h2 className="txt-secao text-tinta">A folha escaneada</h2>
          {imagem.data && imagem.data.mime !== 'application/pdf' ? (
            <>
              <img src={imagem.data.url} alt={`Folha ${folha.codigo} escaneada`} className="w-full max-w-[960px] rounded-controle border border-borda-sutil" />
              {/* Ao lado da grade o papel fica menor: a imagem inteira abre à parte. */}
              <a className="txt-apoio self-start text-tinta underline" href={imagem.data.url} target="_blank" rel="noreferrer">
                Abrir a imagem em tamanho real
              </a>
            </>
          ) : imagem.data ? (
            <a className="txt-corpo self-start text-tinta underline" href={imagem.data.url} target="_blank" rel="noreferrer">
              Abrir a folha escaneada (PDF)
            </a>
          ) : imagem.isError ? (
            <p className="txt-apoio text-tinta-secundaria">A imagem não abriu. Confira pelo papel.</p>
          ) : (
            /* Carregando: um bloco na proporção da folha (p.32). */
            <Carregando oQue="Carregando a imagem da folha">
              <Barra className="aspect-[210/297] w-full max-w-[960px] rounded-card" />
            </Carregando>
          )}
        </section>

        <section className="flex min-w-0 flex-col gap-3">
          <h2 className="txt-secao text-tinta">O que a IA leu</h2>
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            Campo marcado com “conferir” foi lido com dúvida. Hora em branco vale zero. Desmarque a linha que não existe no
            papel; acrescente a que a leitura não viu. O que você corrige fica guardado neste navegador até lançar.
          </p>
          <p className="txt-corpo text-tinta">{contar(incluidas, 'linha marcada para lançar', 'linhas marcadas para lançar')}</p>
          {/* A GRADE DA p.18: seis colunas (no máximo oito), cabeçalho
              rebaixado com os rótulos por extenso, e a primeira coluna — a
              linha do papel — fixa ao rolar de lado. Os dois horímetros numa
              célula, as quatro horas do turno em outra. */}
          <div className="cartao overflow-x-auto">
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr className="bg-rebaixada">
                  <th scope="col" className="txt-legenda sticky left-0 z-10 whitespace-nowrap bg-rebaixada px-4 py-2 text-left align-bottom text-tinta-secundaria">
                    Linha
                  </th>
                  <th scope="col" className="txt-legenda whitespace-nowrap px-4 py-2 text-left align-bottom text-tinta-secundaria">
                    Data
                  </th>
                  <th scope="col" className="txt-legenda whitespace-nowrap px-4 py-2 text-left align-bottom text-tinta-secundaria">
                    Turno
                  </th>
                  <th scope="col" className="whitespace-nowrap px-4 py-2 text-left align-bottom">
                    <span className="txt-legenda block text-tinta-secundaria">Horímetro</span>
                    <span className="mt-1 flex gap-2">
                      {HORIMETROS.map(([campo, rotulo]) => (
                        <span key={campo} className={`txt-ajuda ${LARGURA_DO_HORIMETRO} text-right`}>
                          {rotulo}
                        </span>
                      ))}
                    </span>
                  </th>
                  <th scope="col" className="whitespace-nowrap px-4 py-2 text-left align-bottom">
                    <span className="txt-legenda block text-tinta-secundaria">Divisão do turno (h)</span>
                    <span className="mt-1 flex gap-2">
                      {HORAS_DO_TURNO.map(([campo, rotulo]) => (
                        <span key={campo} className={`txt-ajuda ${LARGURA_DA_HORA} text-right`}>
                          {rotulo}
                        </span>
                      ))}
                    </span>
                  </th>
                  <th scope="col" className="txt-legenda whitespace-nowrap px-4 py-2 text-left align-bottom text-tinta-secundaria">
                    Operador
                  </th>
                  <th scope="col" className="txt-legenda whitespace-nowrap px-4 py-2 text-left align-bottom text-tinta-secundaria">
                    Causa da parada
                  </th>
                </tr>
              </thead>
              <tbody>
                {atuais.map((l, i) => (
                  <LinhaDoTurno
                    key={l.chave}
                    prefixo={idDaLinha(l.chave)}
                    linha={l}
                    indice={i}
                    pessoas={pessoas}
                    turnos={turnos}
                    causas={causas}
                    erro={recusa !== null && recusa.chave === l.chave ? recusa : null}
                    aoMudar={(c) => {
                      mudar(l.chave, c);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {atuais.length === 0 ? (
            <p className="txt-apoio text-tinta-secundaria">A leitura não achou turno preenchido. Acrescente as linhas do papel.</p>
          ) : null}
          <div>
            <Botao
              nivel="terciario"
              onClick={() => {
                /* A linha nova já vem com o que a de cima diz: o horímetro
                   final dela vira o inicial, o turno e o operador se repetem e
                   a data é o dia seguinte (`turnoSeguinte`). */
                setLinhas([...atuais, turnoSeguinte(atuais.at(-1), atuais.length + 1, folha)]);
              }}
            >
              Acrescentar linha
            </Botao>
          </div>
        </section>
      </div>
    </>
  );
}

/* As larguras das caixas de número: o rótulo do cabeçalho fica em cima de
   cada uma, na mesma largura, e cabe por extenso ("Deslocamento"). */
const LARGURA_DO_HORIMETRO = 'w-28';
const LARGURA_DA_HORA = 'w-24';

type CampoDeNumero = CampoDoTurno & keyof TurnoEmConferencia;

const HORIMETROS: readonly (readonly [CampoDeNumero, string])[] = [
  ['horimetroInicial', 'Inicial'],
  ['horimetroFinal', 'Final'],
];

const HORAS_DO_TURNO: readonly (readonly [CampoDeNumero, string])[] = [
  ['producao', 'Produção'],
  ['espera', 'Espera'],
  ['deslocamento', 'Deslocamento'],
  ['manutencao', 'Manutenção'],
];

function LinhaDoTurno({
  prefixo,
  linha,
  indice,
  pessoas,
  turnos,
  causas,
  erro,
  aoMudar,
}: {
  /** Base dos ids dos campos da linha: é por ele que a conferência leva o foco ao campo recusado. */
  readonly prefixo: string;
  readonly linha: TurnoEmConferencia;
  readonly indice: number;
  readonly pessoas: readonly { id: string; nome: string }[];
  readonly turnos: readonly string[];
  readonly causas: readonly CausaDaFolha[];
  readonly erro: RecusaDaLinha | null;
  readonly aoMudar: (campo: Partial<TurnoEmConferencia>) => void;
}) {
  const numeroDaLinha = String(linha.origem ?? indice + 1);
  const idDoErro = `${prefixo}-erro`;
  const duvida = (c: CampoDoTurno) => linha.duvidas.includes(c);
  const recusado = (c: CampoDoTurno) => erro !== null && erro.campos.includes(c);
  const marca = (c: CampoDoTurno) =>
    duvida(c) ? (
      <span id={`${prefixo}-${c}-duvida`} className="txt-apoio text-tinta">
        conferir
      </span>
    ) : null;
  const propsDoCampo = (c: CampoDoTurno) => {
    const descrito = [duvida(c) ? `${prefixo}-${c}-duvida` : null, recusado(c) ? idDoErro : null].filter(Boolean).join(' ');
    return {
      id: `${prefixo}-${c}`,
      'aria-label': `${ROTULO_DO_CAMPO_DO_TURNO[c]} da linha ${numeroDaLinha}`,
      ...(descrito ? { 'aria-describedby': descrito } : {}),
      ...(recusado(c) ? { 'aria-invalid': true as const } : {}),
      disabled: !linha.incluir,
    };
  };
  /* A FRASE DA RECUSA SOB A CÉLULA do campo recusado (p.16; R01, p.28), em
     vermelho como a do Campo — e não num parágrafo no alto da tela. */
  const erroNaCelula = (grupo: readonly CampoDoTurno[]) =>
    erro !== null && grupo.some((c) => erro.campos.includes(c)) ? (
      <span id={idDoErro} role="alert" className="txt-apoio max-w-96 whitespace-normal text-pretty text-parada">
        {erro.frase}
      </span>
    ) : null;
  /* O turno escrito que não casou com um da empresa entra na lista, para quem
     confere escolher entre ele e os cadastrados. Empresa sem turno cadastrado
     não tem lista: o turno se escreve, como no formulário de apontamento. */
  const extras = [linha.turnoEscrito, linha.turno].filter((t): t is string => Boolean(t) && !turnos.includes(t ?? ''));
  const opcoesDeTurno = [...new Set([...turnos, ...extras])];
  const celula = 'border-t border-borda-sutil px-4 py-2 align-top';
  const numero = (campo: CampoDeNumero, largura: string) => (
    <div key={campo} className="flex flex-col items-end gap-1">
      <input
        {...propsDoCampo(campo)}
        inputMode="decimal"
        className={`controle ${largura} text-right tabular-nums`}
        value={linha[campo]}
        onChange={(e) => {
          aoMudar({ [campo]: e.target.value });
        }}
      />
      {marca(campo)}
    </div>
  );
  return (
    <tr className={linha.incluir ? '' : 'text-tinta-apagada'}>
      {/* A primeira coluna é a identidade da linha e não some ao rolar (p.18). */}
      <td className={`${celula} sticky left-0 z-10 bg-card`}>
        <label className="flex h-[var(--controle-m)] items-center gap-2 whitespace-nowrap">
          <input
            type="checkbox"
            aria-label={`Lançar a linha ${numeroDaLinha}`}
            checked={linha.incluir}
            onChange={(e) => {
              aoMudar({ incluir: e.target.checked });
            }}
          />
          <span className="txt-corpo tabular-nums">{linha.origem === null ? 'nova' : String(linha.origem)}</span>
        </label>
      </td>
      <td className={celula}>
        <div className="flex flex-col gap-1">
          <input {...propsDoCampo('data')} type="date" className="controle" value={linha.data} onChange={(e) => { aoMudar({ data: e.target.value }); }} />
          {marca('data')}
          {erroNaCelula(['data'])}
        </div>
      </td>
      <td className={celula}>
        <div className="flex flex-col gap-1">
          {turnos.length === 0 ? (
            <input
              {...propsDoCampo('turno')}
              className="controle w-28"
              placeholder="1º turno"
              maxLength={60}
              value={linha.turno}
              onChange={(e) => { aoMudar({ turno: e.target.value }); }}
            />
          ) : (
            <select {...propsDoCampo('turno')} className="controle" value={linha.turno} onChange={(e) => { aoMudar({ turno: e.target.value }); }}>
              <option value="">Escolha</option>
              {opcoesDeTurno.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          )}
          {linha.turnoEscrito ? <span className="txt-apoio text-tinta-secundaria">{`No papel: ${linha.turnoEscrito}`}</span> : null}
          {marca('turno')}
          {erroNaCelula(['turno'])}
        </div>
      </td>
      <td className={celula}>
        <div className="flex flex-col gap-1">
          <div className="flex items-start gap-2">{HORIMETROS.map(([campo]) => numero(campo, LARGURA_DO_HORIMETRO))}</div>
          {erroNaCelula(HORIMETROS.map(([campo]) => campo))}
        </div>
      </td>
      <td className={celula}>
        <div className="flex flex-col gap-1">
          <div className="flex items-start gap-2">{HORAS_DO_TURNO.map(([campo]) => numero(campo, LARGURA_DA_HORA))}</div>
          {erroNaCelula(HORAS_DO_TURNO.map(([campo]) => campo))}
        </div>
      </td>
      <td className={celula}>
        <div className="flex flex-col gap-1">
          {/* O SELETOR DO SISTEMA, e não o nativo (31/10/2026): a lista das
              pessoas é longa, e o seletor busca pelo nome e mostra o avatar. */}
          <div className="w-56">
            <ListaDeEscolha
              id={`${prefixo}-operador`}
              rotulo={`${ROTULO_DO_CAMPO_DO_TURNO.operador} da linha ${numeroDaLinha}`}
              valor={linha.operadorId}
              aoMudar={(valor) => { aoMudar({ operadorId: valor }); }}
              rotuloVazio="Sem operador"
              desabilitado={!linha.incluir}
              opcoes={pessoas.map((p) => opcaoDaPessoa(p))}
            />
          </div>
          {linha.operadorEscrito ? <span className="txt-apoio text-tinta-secundaria">{`No papel: ${linha.operadorEscrito}`}</span> : null}
          {marca('operador')}
        </div>
      </td>
      {/* A CAUSA DA PARADA (0086): o código do catálogo, na linha com espera
          ou manutenção. O que estava escrito fica embaixo, como no operador. */}
      <td className={celula}>
        <div className="flex flex-col gap-1">
          <div className="w-56">
            <ListaDeEscolha
              id={`${prefixo}-causa`}
              rotulo={`${ROTULO_DO_CAMPO_DO_TURNO.causa} da linha ${numeroDaLinha}`}
              valor={linha.causaId}
              aoMudar={(valor) => { aoMudar({ causaId: valor }); }}
              rotuloVazio="Sem causa"
              desabilitado={!linha.incluir}
              idDoErro={recusado('causa') ? idDoErro : undefined}
              opcoes={causas.map((c) => ({ valor: c.id, rotulo: c.nome, etiquetas: [{ texto: c.codigo, tom: 'neutro' as const, titulo: 'Código da causa' }] }))}
            />
          </div>
          {linha.causaEscrita ? <span className="txt-apoio text-tinta-secundaria">{`No papel: ${linha.causaEscrita}`}</span> : null}
          {marca('causa')}
          {erroNaCelula(['causa'])}
        </div>
      </td>
    </tr>
  );
}
